import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogActions, DialogContent, DialogTitle, Button, CircularProgress, 
  Alert, Grid, TextField, Switch, FormControlLabel, Typography,
  MenuItem, Box, Divider, Checkbox, FormGroup, FormLabel, Chip
} from '@mui/material';
import { supabase } from '../supabaseClient';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';

// Helper to safely convert form values to numbers or null
const safeParseInt = (value) => (value === '' || value === null || value === undefined) ? null : parseInt(value, 10);
const safeParseFloat = (value) => (value === '' || value === null || value === undefined) ? null : parseFloat(value);

const CompanySubscriptionManager = ({ open, onClose, subscription, onSave, companyName }) => {
  const [formState, setFormState] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  // NEW: State for LLM models
  const [availableModels, setAvailableModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [selectedModels, setSelectedModels] = useState([]);

  useEffect(() => {
    if (subscription) {
      setFormState({
        package_name: subscription.packages?.name || subscription.package_name || 'N/A',
        status: subscription.status || 'active',
        trial_ends_at: subscription.trial_ends_at ? new Date(subscription.trial_ends_at) : null,
        current_period_start: subscription.current_period_start ? new Date(subscription.current_period_start) : null,
        current_period_end: subscription.current_period_end ? new Date(subscription.current_period_end) : null,

        limit_monthly_tokens: subscription.limit_monthly_tokens !== null ? String(subscription.limit_monthly_tokens) : '',
        limit_monthly_cost: subscription.limit_monthly_cost !== null ? String(subscription.limit_monthly_cost) : '',
        limit_monthly_messages: subscription.limit_monthly_messages !== null ? String(subscription.limit_monthly_messages) : '',
        limit_daily_messages: subscription.limit_daily_messages !== null ? String(subscription.limit_daily_messages) : '',
        limit_monthly_sessions: subscription.limit_monthly_sessions !== null ? String(subscription.limit_monthly_sessions) : '',
        limit_team_members: subscription.limit_team_members !== null ? String(subscription.limit_team_members) : '',
        daily_token_limit: subscription.daily_token_limit !== null ? String(subscription.daily_token_limit) : '',
        daily_cost_limit: subscription.daily_cost_limit !== null ? String(subscription.daily_cost_limit) : '',

        limit_monthly_searches: subscription.limit_monthly_searches !== null ? String(subscription.limit_monthly_searches) : '',
        limit_daily_searches: subscription.limit_daily_searches !== null ? String(subscription.limit_daily_searches) : '',
        limit_monthly_deep_searches: subscription.limit_monthly_deep_searches !== null ? String(subscription.limit_monthly_deep_searches) : '',
        limit_daily_deep_searches: subscription.limit_daily_deep_searches !== null ? String(subscription.limit_daily_deep_searches) : '',
        
        // Rate Limiting & Performance
        requests_per_minute: subscription.requests_per_minute !== null ? String(subscription.requests_per_minute) : '',
        requests_per_hour: subscription.requests_per_hour !== null ? String(subscription.requests_per_hour) : '',
        per_request_token_limit: subscription.per_request_token_limit !== null ? String(subscription.per_request_token_limit) : '',
        per_request_cost_limit: subscription.per_request_cost_limit !== null ? String(subscription.per_request_cost_limit) : '',
        hourly_message_limit: subscription.hourly_message_limit !== null ? String(subscription.hourly_message_limit) : '',
        concurrent_requests_limit: subscription.concurrent_requests_limit !== null ? String(subscription.concurrent_requests_limit) : '',
        burst_limit: subscription.burst_limit !== null ? String(subscription.burst_limit) : '',
        max_active_sessions: subscription.max_active_sessions !== null ? String(subscription.max_active_sessions) : '',
        max_conversation_length: subscription.max_conversation_length !== null ? String(subscription.max_conversation_length) : '',
        
        // File & Upload Limits
        max_file_size_mb: subscription.max_file_size_mb !== null ? String(subscription.max_file_size_mb) : '',
        max_files_per_upload: subscription.max_files_per_upload !== null ? String(subscription.max_files_per_upload) : '',
        limit_total_files: subscription.limit_total_files !== null ? String(subscription.limit_total_files) : '',
        limit_total_upload_mb: subscription.limit_total_upload_mb !== null && subscription.limit_total_upload_mb !== undefined ? String(subscription.limit_total_upload_mb) : '',
        monthly_upload_limit_mb: subscription.monthly_upload_limit_mb !== null ? String(subscription.monthly_upload_limit_mb) : '',
        allowed_file_types: subscription.allowed_file_types ? JSON.stringify(subscription.allowed_file_types) : '["pdf", "txt", "docx", "md"]',

        // Billing & Trial
        trial_period_days: subscription.trial_period_days !== null ? String(subscription.trial_period_days) : '',
        price_usd: subscription.price_usd !== null ? String(subscription.price_usd) : '',
        price_per_extra_token: subscription.price_per_extra_token !== null ? String(subscription.price_per_extra_token) : '',

        // Data & Caching
        chat_history_retention_days: subscription.chat_history_retention_days !== null ? String(subscription.chat_history_retention_days) : '',
        quota_cache_ttl_seconds: subscription.quota_cache_ttl_seconds !== null ? String(subscription.quota_cache_ttl_seconds) : '',

        // Core Feature Flags
        api_access_enabled: subscription.api_access_enabled || false,
        premium_models_enabled: subscription.premium_models_enabled || false,
        custom_model_access: subscription.custom_model_access || false,
        team_collaboration_enabled: subscription.team_collaboration_enabled || false,
        chatwoot_integration_enabled: subscription.chatwoot_integration_enabled || false,
        overage_allowed: subscription.overage_allowed || false,
        cancel_at_period_end: subscription.cancel_at_period_end || false,
        allow_manual_session_deletion: subscription.allow_manual_session_deletion || false,
        
        // Additional Feature Flags
        webhook_enabled: subscription.webhook_enabled || false,
        analytics_enabled: subscription.analytics_enabled !== undefined ? subscription.analytics_enabled : true,
        export_data_enabled: subscription.export_data_enabled || false,
        billing_enabled: subscription.billing_enabled !== undefined ? subscription.billing_enabled : true,
        auto_upgrade_enabled: subscription.auto_upgrade_enabled || false,
        
        // Enterprise Features
        custom_branding_enabled: subscription.custom_branding_enabled || false,
        white_label_enabled: subscription.white_label_enabled || false,
        sso_enabled: subscription.sso_enabled || false,
        audit_logs_enabled: subscription.audit_logs_enabled || false,
        
        // Configuration & Metadata
        features: subscription.features ? JSON.stringify(subscription.features) : '{}',
        custom_config: subscription.custom_config ? JSON.stringify(subscription.custom_config) : '{}',
        sort_order: subscription.sort_order !== null ? String(subscription.sort_order) : '',
        is_popular: subscription.is_popular || false,
        is_enterprise: subscription.is_enterprise || false,
        
        // NEW: Handle allowed_models
        allowed_models: subscription.allowed_models ? JSON.stringify(subscription.allowed_models) : '[]',
      });
      
      // NEW: Set selected models from subscription data
      try {
        const modelsArray = subscription.allowed_models ? (Array.isArray(subscription.allowed_models) ? subscription.allowed_models : JSON.parse(subscription.allowed_models)) : [];
        setSelectedModels(modelsArray);
      } catch (error) {
        console.error("Error parsing allowed_models:", error);
        setSelectedModels([]);
      }
    }
  }, [subscription]);

  // NEW: Fetch available LLM models
  const fetchAvailableModels = useCallback(async () => {
    if (!open) return; // Only fetch when dialog is open
    
    setLoadingModels(true);
    try {
      const { data, error } = await supabase
        .from('llm_models')
        .select('model_identifier, display_label, is_premium, is_active, provider')
        .eq('is_active', true)
        .order('provider', { ascending: true })
        .order('display_label', { ascending: true });
      
      if (error) throw error;
      setAvailableModels(data || []);
    } catch (error) {
      console.error("Error fetching LLM models:", error);
      setError(`Failed to load available models: ${error.message}`);
    } finally {
      setLoadingModels(false);
    }
  }, [open]);

  useEffect(() => {
    fetchAvailableModels();
  }, [fetchAvailableModels]);

  const handleDateChange = (name, date) => {
    setFormState(prev => ({ ...prev, [name]: date }));
  };

  const handleFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormState(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // NEW: Handle model selection
  const handleModelToggle = (modelIdentifier) => {
    setSelectedModels(prev => {
      const isSelected = prev.includes(modelIdentifier);
      const newSelection = isSelected 
        ? prev.filter(id => id !== modelIdentifier)
        : [...prev, modelIdentifier];
      
      // Update the form field as well
      setFormState(prevForm => ({
        ...prevForm,
        allowed_models: JSON.stringify(newSelection)
      }));
      
      return newSelection;
    });
  };

  const handleSelectAllModels = (checked) => {
    const newSelection = checked ? availableModels.map(model => model.model_identifier) : [];
    setSelectedModels(newSelection);
    setFormState(prev => ({
      ...prev,
      allowed_models: JSON.stringify(newSelection)
    }));
  };

  const handleSaveChanges = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const payload = {
        status: formState.status,
        trial_ends_at: formState.trial_ends_at ? formState.trial_ends_at.toISOString() : null,
        current_period_end: formState.current_period_end ? formState.current_period_end.toISOString() : null,

        // Core Limits
        limit_monthly_tokens: safeParseInt(formState.limit_monthly_tokens),
        limit_monthly_cost: safeParseFloat(formState.limit_monthly_cost),
        limit_monthly_messages: safeParseInt(formState.limit_monthly_messages),
        limit_daily_messages: safeParseInt(formState.limit_daily_messages),
        limit_monthly_sessions: safeParseInt(formState.limit_monthly_sessions),
        limit_team_members: safeParseInt(formState.limit_team_members),
        daily_token_limit: safeParseInt(formState.daily_token_limit),
        daily_cost_limit: safeParseFloat(formState.daily_cost_limit),
        
        // Search Limits
        limit_monthly_searches: safeParseInt(formState.limit_monthly_searches),
        limit_daily_searches: safeParseInt(formState.limit_daily_searches),
        limit_monthly_deep_searches: safeParseInt(formState.limit_monthly_deep_searches),
        limit_daily_deep_searches: safeParseInt(formState.limit_daily_deep_searches),

        // Rate Limiting & Performance
        requests_per_minute: safeParseInt(formState.requests_per_minute),
        requests_per_hour: safeParseInt(formState.requests_per_hour),
        per_request_token_limit: safeParseInt(formState.per_request_token_limit),
        per_request_cost_limit: safeParseFloat(formState.per_request_cost_limit),
        hourly_message_limit: safeParseInt(formState.hourly_message_limit),
        concurrent_requests_limit: safeParseInt(formState.concurrent_requests_limit),
        burst_limit: safeParseInt(formState.burst_limit),
        max_active_sessions: safeParseInt(formState.max_active_sessions),
        max_conversation_length: safeParseInt(formState.max_conversation_length),
        
        // File & Upload Limits
        max_file_size_mb: safeParseInt(formState.max_file_size_mb),
        max_files_per_upload: safeParseInt(formState.max_files_per_upload),
        limit_total_files: safeParseInt(formState.limit_total_files),
        limit_total_upload_mb: safeParseFloat(formState.limit_total_upload_mb),
        monthly_upload_limit_mb: safeParseInt(formState.monthly_upload_limit_mb),
        allowed_file_types: formState.allowed_file_types ? JSON.parse(formState.allowed_file_types) : null,
        allowed_models: formState.allowed_models ? JSON.parse(formState.allowed_models) : null, // NEW: Parse JSON array

        // Billing & Trial
        trial_period_days: safeParseInt(formState.trial_period_days),
        price_usd: safeParseFloat(formState.price_usd),
        price_per_extra_token: safeParseFloat(formState.price_per_extra_token),
        
        // Data & Caching
        chat_history_retention_days: safeParseInt(formState.chat_history_retention_days),
        quota_cache_ttl_seconds: safeParseInt(formState.quota_cache_ttl_seconds),
        
        // Core Feature Flags
        api_access_enabled: formState.api_access_enabled,
        premium_models_enabled: formState.premium_models_enabled,
        custom_model_access: formState.custom_model_access,
        team_collaboration_enabled: formState.team_collaboration_enabled,
        chatwoot_integration_enabled: formState.chatwoot_integration_enabled,
        overage_allowed: formState.overage_allowed,
        cancel_at_period_end: formState.cancel_at_period_end,
        allow_manual_session_deletion: formState.allow_manual_session_deletion,
        
        // Additional Feature Flags
        webhook_enabled: formState.webhook_enabled,
        analytics_enabled: formState.analytics_enabled,
        export_data_enabled: formState.export_data_enabled,
        billing_enabled: formState.billing_enabled,
        auto_upgrade_enabled: formState.auto_upgrade_enabled,
        
        // Enterprise Features
        custom_branding_enabled: formState.custom_branding_enabled,
        white_label_enabled: formState.white_label_enabled,
        sso_enabled: formState.sso_enabled,
        audit_logs_enabled: formState.audit_logs_enabled,
        
        // Configuration & Metadata
        features: formState.features ? JSON.parse(formState.features) : {},
        custom_config: formState.custom_config ? JSON.parse(formState.custom_config) : {},
        sort_order: safeParseInt(formState.sort_order),
        is_popular: formState.is_popular,
        is_enterprise: formState.is_enterprise,
      };
      await onSave(payload);
      onClose();
    } catch (err) {
      setError(err.message || "An error occurred while saving.");
    } finally {
      setIsSaving(false);
    }
  };

  // Allow opening dialog for admins even when there is no subscription yet
  // Render a minimal shell with an info message and allow Save to no-op
  if (!subscription) {
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
          <DialogTitle>Manage Subscription for {companyName || 'Company'}</DialogTitle>
          <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Typography variant="body2" sx={{ mt: 1 }}>
              No active subscription found. You can assign a package from the user page,
              or create a subscription via admin tools.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: '16px 24px'}}>
            <Button onClick={onClose}>Close</Button>
          </DialogActions>
        </Dialog>
      </LocalizationProvider>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>Manage Subscription for {companyName || 'Company'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <Typography variant="h6" sx={{mt: 2}}>Package: {formState.package_name}</Typography>
            <TextField
                margin="dense" name="status" label="Subscription Status"
                value={formState.status} onChange={handleFormChange}
                select sx={{minWidth: 200}}
            >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="trialing">Trialing</MenuItem>
                <MenuItem value="past_due">Past Due</MenuItem>
                <MenuItem value="canceled">Canceled</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
          </Box>

          {/* Coupon Information Display */}
          {subscription?.stripe_coupon_id && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#e8f5e9', borderRadius: 1, border: '1px solid #4caf50' }}>
              <Typography variant="h6" sx={{ color: '#2e7d32', mb: 1 }}>
                🎉 Coupon Applied: {subscription.stripe_promotion_code || subscription.stripe_coupon_id}
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Coupon Name:</strong> {subscription.coupon_name || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Discount:</strong>{' '}
                    {subscription.coupon_percent_off 
                      ? `${subscription.coupon_percent_off}% off`
                      : subscription.coupon_amount_off 
                        ? `$${(subscription.coupon_amount_off / 100).toFixed(2)} off`
                        : 'N/A'
                    }
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Duration:</strong>{' '}
                    {subscription.coupon_duration === 'forever' && 'Forever'}
                    {subscription.coupon_duration === 'once' && 'One-time'}
                    {subscription.coupon_duration === 'repeating' && 
                      `${subscription.coupon_duration_months} months`
                    }
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Amount Saved:</strong> ${subscription.discount_amount?.toFixed(2) || '0.00'}/month
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2">
                    <strong>Applied On:</strong>{' '}
                    {subscription.coupon_applied_at 
                      ? new Date(subscription.coupon_applied_at).toLocaleDateString()
                      : 'N/A'
                    }
                  </Typography>
                </Grid>
              </Grid>
              <Alert severity="info" sx={{ mt: 1, fontSize: '0.85rem' }}>
                <strong>Note:</strong> Coupons are managed in Stripe Dashboard. This is read-only display.
              </Alert>
            </Box>
          )}

          <Typography variant="h6" sx={{mt: 3, mb: 2}}>Dates & Periods</Typography>
          <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                  <DateTimePicker
                      label="Trial Ends At"
                      value={formState.trial_ends_at}
                      onChange={(date) => handleDateChange('trial_ends_at', date)}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                  />
              </Grid>
              <Grid item xs={12} sm={4}>
                  <DateTimePicker
                      label="Current Period Start"
                      value={formState.current_period_start}
                      onChange={(date) => handleDateChange('current_period_start', date)}
                      renderInput={(params) => <TextField {...params} fullWidth disabled />}
                  />
              </Grid>
              <Grid item xs={12} sm={4}>
                  <DateTimePicker
                      label="Current Period End"
                      value={formState.current_period_end}
                      onChange={(date) => handleDateChange('current_period_end', date)}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                  />
              </Grid>
          </Grid>

          <Typography variant="h6" sx={{mt: 3}}>Core Usage Limits</Typography>
          <Grid container spacing={2} sx={{mt: 1}}>
              <Grid item xs={12} sm={6} md={3}><TextField name="limit_monthly_tokens" label="Monthly Token Limit" type="number" fullWidth value={formState.limit_monthly_tokens} onChange={handleFormChange} /></Grid>
              <Grid item xs={12} sm={6} md={3}><TextField name="daily_token_limit" label="Daily Token Limit" type="number" fullWidth value={formState.daily_token_limit} onChange={handleFormChange} /></Grid>
              <Grid item xs={12} sm={6} md={3}><TextField name="limit_monthly_cost" label="Monthly Cost Limit (USD)" type="number" fullWidth value={formState.limit_monthly_cost} onChange={handleFormChange} /></Grid>
              <Grid item xs={12} sm={6} md={3}><TextField name="daily_cost_limit" label="Daily Cost Limit (USD)" type="number" fullWidth value={formState.daily_cost_limit} onChange={handleFormChange} /></Grid>
              <Grid item xs={12} sm={6} md={3}><TextField name="limit_monthly_messages" label="Monthly Message Limit" type="number" fullWidth value={formState.limit_monthly_messages} onChange={handleFormChange} /></Grid>
              <Grid item xs={12} sm={6} md={3}><TextField name="limit_daily_messages" label="Daily Message Limit" type="number" fullWidth value={formState.limit_daily_messages} onChange={handleFormChange} /></Grid>
              <Grid item xs={12} sm={6} md={3}><TextField name="limit_monthly_sessions" label="Monthly Session Limit" type="number" fullWidth value={formState.limit_monthly_sessions} onChange={handleFormChange} /></Grid>
              <Grid item xs={12} sm={6} md={3}><TextField name="limit_team_members" label="Team Member Limit" type="number" fullWidth value={formState.limit_team_members} onChange={handleFormChange} /></Grid>
          </Grid>
          
          <Typography variant="h6" sx={{mt: 3}}>Search Limits</Typography>
          <Grid container spacing={2} sx={{mt: 1}}>
              <Grid item xs={12} sm={6} md={3}><TextField name="limit_monthly_searches" label="Monthly Searches" type="number" fullWidth value={formState.limit_monthly_searches} onChange={handleFormChange} /></Grid>
              <Grid item xs={12} sm={6} md={3}><TextField name="limit_daily_searches" label="Daily Searches" type="number" fullWidth value={formState.limit_daily_searches} onChange={handleFormChange} /></Grid>
              <Grid item xs={12} sm={6} md={3}><TextField name="limit_monthly_deep_searches" label="Monthly Deep Searches" type="number" fullWidth value={formState.limit_monthly_deep_searches} onChange={handleFormChange} /></Grid>
              <Grid item xs={12} sm={6} md={3}><TextField name="limit_daily_deep_searches" label="Daily Deep Searches" type="number" fullWidth value={formState.limit_daily_deep_searches} onChange={handleFormChange} /></Grid>
          </Grid>

          <Typography variant="h6" sx={{mt: 3}}>Rate & File Limits</Typography>
          <Grid container spacing={2} sx={{mt: 1}}>
              <Grid item xs={12} sm={6} md={3}><TextField name="requests_per_minute" label="Reqs / Min" type="number" fullWidth value={formState.requests_per_minute} onChange={handleFormChange} /></Grid>
              <Grid item xs={12} sm={6} md={3}><TextField name="requests_per_hour" label="Reqs / Hour" type="number" fullWidth value={formState.requests_per_hour} onChange={handleFormChange} /></Grid>
              <Grid item xs={12} sm={6} md={3}><TextField name="max_file_size_mb" label="Max File Size (MB)" type="number" fullWidth value={formState.max_file_size_mb} onChange={handleFormChange} /></Grid>
              <Grid item xs={12} sm={6} md={3}><TextField name="max_files_per_upload" label="Max Files / Upload" type="number" fullWidth value={formState.max_files_per_upload} onChange={handleFormChange} /></Grid>
              <Grid item xs={12} sm={6} md={3}><TextField name="limit_total_files" label="Total Files Limit (Company)" type="number" fullWidth value={formState.limit_total_files} onChange={handleFormChange} helperText="-1 or empty = unlimited" /></Grid>
              <Grid item xs={12} sm={6} md={3}><TextField name="limit_total_upload_mb" label="Total Upload Limit (MB)" type="number" fullWidth value={formState.limit_total_upload_mb || ''} onChange={handleFormChange} helperText="empty = unlimited" /></Grid>
          </Grid>

          <Typography variant="h6" sx={{mt: 3}}>Data & Caching</Typography>
          <Grid container spacing={2} sx={{mt: 1}}>
              <Grid item xs={12} sm={6}><TextField name="chat_history_retention_days" label="Chat History Retention (Days)" type="number" fullWidth value={formState.chat_history_retention_days} onChange={handleFormChange} /></Grid>
              <Grid item xs={12} sm={6}><TextField name="quota_cache_ttl_seconds" label="Quota Cache TTL (Seconds)" type="number" fullWidth value={formState.quota_cache_ttl_seconds} onChange={handleFormChange} /></Grid>
          </Grid>

          <Typography variant="h6" sx={{mt: 3}}>Feature Flags</Typography>
          <Grid container spacing={1}>
              <Grid item xs={12} sm={4} md={3}><FormControlLabel control={<Switch checked={formState.api_access_enabled || false} onChange={handleFormChange} name="api_access_enabled" />} label="API Access" /></Grid>
              <Grid item xs={12} sm={4} md={3}><FormControlLabel control={<Switch checked={formState.premium_models_enabled || false} onChange={handleFormChange} name="premium_models_enabled" />} label="Premium Models" /></Grid>
              <Grid item xs={12} sm={4} md={3}><FormControlLabel control={<Switch checked={formState.team_collaboration_enabled || false} onChange={handleFormChange} name="team_collaboration_enabled" />} label="Team Collaboration" /></Grid>
              <Grid item xs={12} sm={4} md={3}><FormControlLabel control={<Switch checked={formState.chatwoot_integration_enabled || false} onChange={handleFormChange} name="chatwoot_integration_enabled" />} label="Chatwoot Integration" /></Grid>
              <Grid item xs={12} sm={4} md={3}><FormControlLabel control={<Switch checked={formState.overage_allowed || false} onChange={handleFormChange} name="overage_allowed" />} label="Overage Allowed" /></Grid>
              <Grid item xs={12} sm={4} md={3}><FormControlLabel control={<Switch checked={formState.cancel_at_period_end || false} onChange={handleFormChange} name="cancel_at_period_end" />} label="Cancel at Period End" /></Grid>
              <Grid item xs={12} sm={4} md={3}><FormControlLabel control={<Switch checked={formState.allow_manual_session_deletion || false} onChange={handleFormChange} name="allow_manual_session_deletion" />} label="Allow Manual Session Deletion" /></Grid>
          </Grid>

          {/* NEW MISSING FIELDS - Rate Limiting & Sessions */}
          <Typography variant="h6" sx={{mt: 3}}>Rate Limiting & Sessions</Typography>
          <Grid container spacing={2} sx={{mt: 1}}>
              <Grid item xs={12} sm={6} md={3}><TextField name="per_request_token_limit" label="Per Request Token Limit" type="number" fullWidth value={formState.per_request_token_limit || ''} onChange={handleFormChange} /></Grid>
              <Grid item xs={12} sm={6} md={3}><TextField name="per_request_cost_limit" label="Per Request Cost Limit" type="number" fullWidth value={formState.per_request_cost_limit || ''} onChange={handleFormChange} /></Grid>
              <Grid item xs={12} sm={6} md={3}><TextField name="hourly_message_limit" label="Hourly Message Limit" type="number" fullWidth value={formState.hourly_message_limit || ''} onChange={handleFormChange} /></Grid>
              <Grid item xs={12} sm={6} md={3}><TextField name="concurrent_requests_limit" label="Concurrent Requests Limit" type="number" fullWidth value={formState.concurrent_requests_limit || ''} onChange={handleFormChange} /></Grid>
              <Grid item xs={12} sm={6} md={3}><TextField name="burst_limit" label="Burst Limit" type="number" fullWidth value={formState.burst_limit || ''} onChange={handleFormChange} /></Grid>
              <Grid item xs={12} sm={6} md={3}><TextField name="max_active_sessions" label="Max Active Sessions" type="number" fullWidth value={formState.max_active_sessions || ''} onChange={handleFormChange} /></Grid>
              <Grid item xs={12} sm={6} md={3}><TextField name="max_conversation_length" label="Max Conversation Length" type="number" fullWidth value={formState.max_conversation_length || ''} onChange={handleFormChange} /></Grid>
          </Grid>

          {/* File & Upload Limits */}
          <Typography variant="h6" sx={{mt: 3}}>File & Upload Limits</Typography>
          <Grid container spacing={2} sx={{mt: 1}}>
              <Grid item xs={12} sm={6}><TextField name="monthly_upload_limit_mb" label="Monthly Upload Limit (MB)" type="number" fullWidth value={formState.monthly_upload_limit_mb || ''} onChange={handleFormChange} /></Grid>
              <Grid item xs={12} sm={6}><TextField name="trial_period_days" label="Trial Period (Days)" type="number" fullWidth value={formState.trial_period_days || ''} onChange={handleFormChange} /></Grid>
              <Grid item xs={12}><TextField name="allowed_file_types" label="Allowed File Types (JSON)" multiline rows={2} fullWidth value={formState.allowed_file_types || ''} onChange={handleFormChange} helperText="JSON array format, e.g., [&quot;pdf&quot;, &quot;txt&quot;, &quot;docx&quot;]" /></Grid>
          </Grid>

          {/* NEW: LLM Model Selection Section */}
          <Typography variant="h6" sx={{mt: 3}}>Allowed LLM Models</Typography>
          <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, mt: 1 }}>
            {loadingModels ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
                <Typography sx={{ ml: 1 }}>Loading models...</Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedModels.length === availableModels.length && availableModels.length > 0}
                        indeterminate={selectedModels.length > 0 && selectedModels.length < availableModels.length}
                        onChange={(e) => handleSelectAllModels(e.target.checked)}
                      />
                    }
                    label={<Typography variant="body2" sx={{ fontWeight: 'bold' }}>Select All Models</Typography>}
                  />
                </Box>
                <Divider sx={{ mb: 2 }} />
                <FormGroup sx={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {availableModels.map((model) => (
                    <FormControlLabel
                      key={model.model_identifier}
                      control={
                        <Checkbox
                          checked={selectedModels.includes(model.model_identifier)}
                          onChange={() => handleModelToggle(model.model_identifier)}
                          size="small"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2">
                            {model.display_label}
                            {model.is_premium && (
                              <Chip 
                                label="Premium" 
                                size="small" 
                                color="primary" 
                                sx={{ ml: 1, fontSize: '0.7rem', height: '18px' }} 
                              />
                            )}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Provider: {model.provider} • ID: {model.model_identifier}
                          </Typography>
                        </Box>
                      }
                      sx={{ mb: 0.5 }}
                    />
                  ))}
                  {availableModels.length === 0 && (
                    <Typography variant="body2" color="textSecondary" sx={{ p: 1 }}>
                      No active models found. Check your LLM models configuration.
                    </Typography>
                  )}
                </FormGroup>
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                  {selectedModels.length} model(s) selected. Leave empty to allow all models.
                </Typography>
              </>
            )}
          </Box>

          {/* Additional Feature Flags */}
          <Typography variant="h6" sx={{mt: 3}}>Additional Features</Typography>
          <Grid container spacing={1}>
              <Grid item xs={12} sm={4} md={3}><FormControlLabel control={<Switch checked={formState.custom_model_access || false} onChange={handleFormChange} name="custom_model_access" />} label="Custom Model Access" /></Grid>
              <Grid item xs={12} sm={4} md={3}><FormControlLabel control={<Switch checked={formState.webhook_enabled || false} onChange={handleFormChange} name="webhook_enabled" />} label="Webhook Enabled" /></Grid>
              <Grid item xs={12} sm={4} md={3}><FormControlLabel control={<Switch checked={formState.analytics_enabled || false} onChange={handleFormChange} name="analytics_enabled" />} label="Analytics Enabled" /></Grid>
              <Grid item xs={12} sm={4} md={3}><FormControlLabel control={<Switch checked={formState.export_data_enabled || false} onChange={handleFormChange} name="export_data_enabled" />} label="Export Data Enabled" /></Grid>
              <Grid item xs={12} sm={4} md={3}><FormControlLabel control={<Switch checked={formState.priority_support || false} onChange={handleFormChange} name="priority_support" />} label="Priority Support" /></Grid>
              <Grid item xs={12} sm={4} md={3}><FormControlLabel control={<Switch checked={formState.billing_enabled || false} onChange={handleFormChange} name="billing_enabled" />} label="Billing Enabled" /></Grid>
              <Grid item xs={12} sm={4} md={3}><FormControlLabel control={<Switch checked={formState.auto_upgrade_enabled || false} onChange={handleFormChange} name="auto_upgrade_enabled" />} label="Auto Upgrade Enabled" /></Grid>
          </Grid>

          {/* Enterprise Features */}
          <Typography variant="h6" sx={{mt: 3}}>Enterprise Features</Typography>
          <Grid container spacing={1}>
              <Grid item xs={12} sm={4} md={3}><FormControlLabel control={<Switch checked={formState.custom_branding_enabled || false} onChange={handleFormChange} name="custom_branding_enabled" />} label="Custom Branding" /></Grid>
              <Grid item xs={12} sm={4} md={3}><FormControlLabel control={<Switch checked={formState.white_label_enabled || false} onChange={handleFormChange} name="white_label_enabled" />} label="White Label" /></Grid>
              <Grid item xs={12} sm={4} md={3}><FormControlLabel control={<Switch checked={formState.sso_enabled || false} onChange={handleFormChange} name="sso_enabled" />} label="SSO Enabled" /></Grid>
              <Grid item xs={12} sm={4} md={3}><FormControlLabel control={<Switch checked={formState.audit_logs_enabled || false} onChange={handleFormChange} name="audit_logs_enabled" />} label="Audit Logs" /></Grid>
              <Grid item xs={12} sm={4} md={3}><FormControlLabel control={<Switch checked={formState.is_popular || false} onChange={handleFormChange} name="is_popular" />} label="Popular Package" /></Grid>
              <Grid item xs={12} sm={4} md={3}><FormControlLabel control={<Switch checked={formState.is_enterprise || false} onChange={handleFormChange} name="is_enterprise" />} label="Enterprise Package" /></Grid>
          </Grid>

          {/* Advanced Configuration */}
          <Typography variant="h6" sx={{mt: 3}}>Advanced Configuration</Typography>
          <Grid container spacing={2} sx={{mt: 1}}>
              <Grid item xs={12} sm={6}><TextField name="sort_order" label="Sort Order" type="number" fullWidth value={formState.sort_order || ''} onChange={handleFormChange} /></Grid>
              <Grid item xs={12}><TextField name="features" label="Features (JSON)" multiline rows={3} fullWidth value={formState.features || ''} onChange={handleFormChange} helperText="JSON object for additional features" /></Grid>
              <Grid item xs={12}><TextField name="custom_config" label="Custom Config (JSON)" multiline rows={3} fullWidth value={formState.custom_config || ''} onChange={handleFormChange} helperText="JSON object for custom configuration" /></Grid>
          </Grid>
          
        </DialogContent>
        <DialogActions sx={{ p: '16px 24px'}}>
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={handleSaveChanges} variant="contained" disabled={isSaving}>
            {isSaving ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default CompanySubscriptionManager; 